import React from 'react';
import { Link } from 'react-router-dom';
import { 
  stripHtml, 
  estimateReadingTime 
} from '../../services/utils';
import {
  ClockIcon,
  CalendarIcon,
  UserIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import fr from 'date-fns/locale/fr';

const HeroSection = ({ article }) => {
  if (!article) return null;

  const {
    title,
    excerpt,
    slug,
    postDate,
    categories,
    content,
    cleanContent,
    author,
    image
  } = article;

  const readingTime = article.readingTime || estimateReadingTime(content || cleanContent || '');
  const formattedDate = postDate
    ? format(new Date(postDate), 'dd MMMM yyyy', { locale: fr })
    : '';

  return (
    <div className="relative group mt-40">
      {/* Badge "À la une" */}
      <div className="absolute top-4 left-4 z-20">
        <span className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-full shadow-lg">
          <span className="mr-2">⭐</span>
          À LA UNE
        </span>
      </div>

      {/* Badge temps de lecture */}
      <div className="absolute top-4 right-4 z-20">
        <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-slate-700 flex items-center space-x-1">
          <ClockIcon className="w-4 h-4" />
          <span>{readingTime} min</span>
        </div>
      </div>

      {/* Carte principale */}
      <div className="relative h-[400px] md:h-[450px] overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
        
        <div className="absolute inset-0">
          {image ? (
            <img
              src={image}
              alt={stripHtml(title)}
              className="w-full h-full object-contain bg-gray-100"
              style={{ objectPosition: 'center' }}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-center px-4">
              <span className="text-gray-500 text-sm font-semibold line-clamp-2">
                {stripHtml(title)}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Bouton "Lire" */}
        <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
          <div className="bg-red-600 text-white px-3 py-2 rounded-full text-sm font-medium flex items-center space-x-2">
            <span>Lire</span>
            <ArrowRightIcon className="w-4 h-4" />
          </div>
        </div>

        {/* Contenu de l'article */}
        <div className="relative z-10 p-6 md:p-12 h-full flex flex-col justify-end">
          <div className="max-w-4xl">
            {categories && categories.length > 0 && (
              <div className="mb-6">
                <Link
                  to={`/category/${categories[0].slug}`}
                  className="inline-block px-3 py-1 bg-gradient-to-r from-blue-800 to-blue-900 text-white text-sm font-semibold rounded-full hover:from-blue-900 hover:to-blue-800 transition-all duration-300 shadow-lg"
                >
                  {categories[0].name}
                </Link>
              </div>
            )}

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
              <Link 
                to={`/article/${slug}`}
                className="hover:text-red-600 transition-colors duration-300"
              >
                {stripHtml(title)}
              </Link>
            </h1>

            <p className="text-slate-200 text-base md:text-lg mb-6 line-clamp-2 max-w-2xl leading-relaxed">
              {stripHtml(excerpt)}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-300 mb-6">
              {formattedDate && (
                <div className="flex items-center space-x-2">
                  <CalendarIcon className="w-4 h-4 text-blue-400" />
                  <span className="font-medium">{formattedDate}</span>
                </div>
              )}
              {author && (
                <div className="flex items-center space-x-2">
                  <UserIcon className="w-4 h-4 text-red-400" />
                  <span className="font-medium">{author.displayName || author.name}</span>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-300/30">
              <Link
                to={`/article/${slug}`}
                className="inline-flex items-center space-x-2 text-base font-semibold text-white hover:text-red-400 transition-colors duration-300 group"
              >
                <span>Lire l'article </span>
                <ArrowRightIcon className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 via-blue-800 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
      </div>
    </div>
  );
};

export default HeroSection;